import { useQuery } from "@apollo/client/react";

import {
  ValidateDbpjKitWorkerJobRerunDocument,
  type ValidateDbpjKitWorkerJobRerunQuery,
  type ValidateDbpjKitWorkerJobRerunQueryVariables,
} from "~/features/dbpjkitWorkerJobs/gqls";
import { useTenant } from "~/features/tenant/context";

/**
 * Pre-flight check for the rerun dialog via `cnutValidateWorkerJobRerun`: asks
 * Cnut whether the job can be rerun right now (no active run, stored arguments
 * still match the current command schema) and why not. Pass `skip` while the
 * dialog is closed; the verdict depends on live run status, so it re-fetches on
 * every open instead of trusting the cache.
 */
export function useDbpjkitWorkerJobRerunValidation(jobId: string, { skip = false }: { skip?: boolean } = {}) {
  const { tenantId } = useTenant();

  const { data, loading, error } = useQuery<
    ValidateDbpjKitWorkerJobRerunQuery,
    ValidateDbpjKitWorkerJobRerunQueryVariables
  >(ValidateDbpjKitWorkerJobRerunDocument, {
    variables: { input: { tenantId: tenantId ?? "", jobId } },
    skip: skip || !tenantId,
    fetchPolicy: "network-only",
  });

  // useQuery can keep the previous data when a later request errors, so a failed
  // re-check must not fall back to a stale successful verdict: only trust the data
  // when the latest request succeeded.
  const verdict = error ? undefined : data?.cnutValidateWorkerJobRerun;
  return {
    validating: loading,
    // While loading or on a transport error, err on the safe side: not rerunnable.
    rerunnable: verdict?.rerunnable ?? false,
    reasons: verdict?.reasons ?? (error ? [error.message] : []),
    dryRunSupported: verdict?.dryRunSupported ?? false,
  };
}
